export type NavigationSection = {
  label: string;
  anchor: string;
  description: string;
};

export type LifestylePath = {
  id: string;
  title: string;
  shortLabel: string;
  thesis: string;
  bestFor: string[];
  watchouts: string[];
  fieldChecks: string[];
  goodDaySignals: string[];
};

export type Defense = {
  id: string;
  title: string;
  category: "planning" | "scam" | "risk";
  trigger: string;
  practicalMoves: string[];
  specialistBoundary: string;
};

export type ResourceCategory = {
  id: string;
  title: string;
  useFor: string;
  exampleSources: string[];
  questionsToAsk: string[];
};

export type BucketListCategory = {
  id: string;
  title: string;
  principle: string;
  ideas: string[];
  lowFrictionVersion: string;
};

export type FieldNote = {
  id: string;
  title: string;
  category: string;
  summary: string;
  body: string[];
};

export const positioning = {
  name: "Next Chapter Atlas",
  eyebrow: "Retirement is a life-system decision, not a brochure category.",
  headline: "Plan the next chapter around good days, weak spots, and real tradeoffs.",
  subheadline:
    "Next Chapter compares retirement paths by daily friction, healthcare access, social rhythm, family alignment, place risk, and decline optionality.",
  promise:
    "A practical orientation layer for people choosing between staying put, moving near family, trying a 55+ community, retiring abroad, or building a slower health-constrained life.",
  disclaimer:
    "Next Chapter is research and decision support. It does not provide medical, legal, tax, investment, insurance, or regulated financial advice.",
  principles: [
    "Treat every retirement option as an operating system for daily life.",
    "Score failure modes before ranking dream places.",
    "Separate inspiration from irreversible decisions.",
    "Use specialists for specialist calls; use the atlas to know what to ask.",
  ],
  callsToAction: {
    primary: "Compare paths",
    secondary: "Read field notes",
  },
};

export const navigationSections: NavigationSection[] = [
  {
    label: "Paths",
    anchor: "paths",
    description: "Compare the main retirement life systems people actually choose.",
  },
  {
    label: "Defenses",
    anchor: "defenses",
    description: "Planning, scam, and risk checks before a decision hardens.",
  },
  {
    label: "Resources",
    anchor: "resources",
    description: "Where to gather source material without mistaking SEO for truth.",
  },
  {
    label: "Bucket List",
    anchor: "bucket-list",
    description: "Inspiration that still respects energy, mobility, budget, and family reality.",
  },
  {
    label: "Field Notes",
    anchor: "field-notes",
    description: "Starter essays for the prototype blog and research surface.",
  },
];

export const lifestylePaths: LifestylePath[] = [
  {
    id: "rv-seasonal-loop",
    title: "RV Or Seasonal Loop",
    shortLabel: "RV",
    thesis:
      "The RV path buys optionality and novelty, but it also turns retirement into a logistics practice.",
    bestFor: [
      "People who like movement more than rooted local belonging",
      "Couples or solo travelers who can tolerate maintenance surprises",
      "Retirees testing climates before choosing a permanent base",
    ],
    watchouts: [
      "Healthcare continuity can degrade when every appointment is in a different region",
      "Vehicle repairs, campground scarcity, insurance, fuel, and storage can eat the fantasy budget",
      "The path gets harder when driving stamina, night vision, balance, or spouse health changes",
    ],
    fieldChecks: [
      "List routine prescriptions, specialists, and urgent-care backup for each seasonal stop",
      "Run a three-month all-in cost with campground, fuel, maintenance reserve, and storage",
      "Define the off-ramp: where the rig goes if one person cannot travel",
    ],
    goodDaySignals: [
      "Morning walks in new terrain",
      "Repeatable camp neighbors or club loops",
      "Enough slack days that travel does not become work",
    ],
  },
  {
    id: "snowbird-two-base",
    title: "Snowbird Two-Base Life",
    shortLabel: "Snowbird",
    thesis:
      "Snowbirding can solve climate and mood, but it doubles the home, doctor, paperwork, and social maintenance problem.",
    bestFor: [
      "People who want warm winters without abandoning an existing hometown",
      "Couples with family or medical anchors in two regions",
      "Retirees who need a reversible test before a full relocation",
    ],
    watchouts: [
      "Two homes can mean two sets of repairs, bills, neighbors, passwords, and emergency contacts",
      "The sunny base may be optimized for recreation while the original base carries the real support network",
      "Insurance, storms, heat, and car dependence matter more than vacation impressions",
    ],
    fieldChecks: [
      "Decide which base owns primary care, specialists, legal documents, and family emergency response",
      "Map airport routes, drive time, and pet or mobility constraints for the seasonal transition",
      "Test the winter base for six ordinary weeks, not just a resort week",
    ],
    goodDaySignals: [
      "Predictable seasonal rituals",
      "Recurring friends in both places",
      "A clear handoff calendar for homes, vehicles, mail, and medications",
    ],
  },
  {
    id: "retire-abroad",
    title: "Retire Abroad",
    shortLabel: "Abroad",
    thesis:
      "Retiring abroad is not a cheaper version of the same life; it is a new legal, healthcare, language, and distance system.",
    bestFor: [
      "People who want cultural novelty and can handle bureaucracy",
      "Geoarbitrage retirees comparing cost, healthcare access, and lifestyle density",
      "Health-constrained people considering lower-friction daily life in an expat hub",
    ],
    watchouts: [
      "Visa status, insurance eligibility, tax residency, and banking can dominate lifestyle preference",
      "Great routine care does not automatically solve major decline, dementia, or family-distance problems",
      "Loneliness can be hidden by the first year of novelty",
    ],
    fieldChecks: [
      "Run a bureaucracy checklist: visa, tax residency, estate documents, banking, driving, and local address",
      "Identify English-capable routine, urgent, specialist, hospital, dental, and pharmacy options",
      "Create a return plan for hospitalization, spouse death, parent crisis, or visa change",
    ],
    goodDaySignals: [
      "Walkable errands",
      "Affordable help for routine friction",
      "A community that exists outside tourist consumption",
    ],
  },
  {
    id: "fifty-five-plus-community",
    title: "55+ Community",
    shortLabel: "55+",
    thesis:
      "A 55+ community sells a pre-built social and recreation machine; the real question is whether its culture, governance, and decline path fit.",
    bestFor: [
      "People who want peers, amenities, clubs, and lower-maintenance housing",
      "New retirees replacing work's repetition and weak ties",
      "Couples who prefer structured social options over inventing a local network from scratch",
    ],
    watchouts: [
      "HOA fees are also governance, rules, assessments, and politics",
      "Golf, pickleball, clubs, and pools do not guarantee belonging for introverts, singles, or politically mismatched residents",
      "Active-adult marketing may underweight home health, specialist access, driving loss, and spouse decline",
    ],
    fieldChecks: [
      "Talk to residents about complaints, assessments, club culture, and what happens after health changes",
      "Compare mega-city, resort-golf, older affordable stock, new-build master plan, and healthcare-proximate models",
      "Calculate all-in monthly cost under insurance, HOA increase, repair, and transportation stress",
    ],
    goodDaySignals: [
      "Easy recurring social contact",
      "A calendar with more than passive entertainment",
      "Amenities that match actual weekly habits, not aspirational identity",
    ],
  },
  {
    id: "urban-university-town",
    title: "Urban Or University Town",
    shortLabel: "Urban",
    thesis:
      "Cities and university towns can replace work with classes, culture, hospitals, transit, and mixed-age life, but only if the housing and daily friction work.",
    bestFor: [
      "People who want learning, culture, walkability, and younger energy",
      "Retirees who would rather join civic life than an age-segregated community",
      "Solo agers who value transit, hospitals, and frequent third places",
    ],
    watchouts: [
      "Stairs, noise, crime perception, parking, winter weather, and high rents can spoil the theory",
      "University access may be more limited or expensive than the brochure image suggests",
      "Dense places still require a plan for check-ins, home help, and late-life care",
    ],
    fieldChecks: [
      "Walk the actual grocery, pharmacy, park, library, class, and hospital routes at normal weekly hours",
      "Test one month without relying on long drives",
      "List recurring roles: class, volunteering, mentoring, religious group, part-time work, neighborhood routine",
    ],
    goodDaySignals: [
      "Errands without a car",
      "Mixed-age conversations",
      "A weekly calendar that creates usefulness, not just consumption",
    ],
  },
  {
    id: "near-family",
    title: "Stay Near Family",
    shortLabel: "Family",
    thesis:
      "Family proximity can be the strongest safety net or the most expensive source of unclear expectations.",
    bestFor: [
      "Grandparents who want routine contact rather than holiday visits",
      "Retirees whose adult children are willing and able to be part of the support system",
      "Couples choosing a practical base over a fantasy destination",
    ],
    watchouts: [
      "Moving near family without boundaries can turn retirement into unpaid childcare or emergency dependency",
      "Adult children may move, divorce, burn out, or disagree with siblings",
      "A family suburb may be safe but lonely if it lacks peers, transport, and purpose",
    ],
    fieldChecks: [
      "Write the expectation memo: visits, childcare, money boundaries, emergencies, driving, holidays, and care limits",
      "Score the area as if family were unavailable for six weeks",
      "Identify non-family social infrastructure within a 20-minute routine radius",
    ],
    goodDaySignals: [
      "Ordinary shared meals",
      "Clear boundaries",
      "A life that still works when family is busy",
    ],
  },
  {
    id: "health-constrained-slow-life",
    title: "Health-Constrained Slow Life",
    shortLabel: "Slow Life",
    thesis:
      "For some people, the best retirement-like design starts before traditional retirement age: fewer stairs, fewer errands, less heat, better access, and more predictable energy use.",
    bestFor: [
      "Younger people whose body needs a low-friction environment now",
      "Retirees managing fatigue, mobility limits, chronic illness, or driving uncertainty",
      "Couples where one partner's energy envelope defines the real plan",
    ],
    watchouts: [
      "Beautiful places can fail on pharmacies, specialists, elevators, shade, transport, or food access",
      "A low-cost place can become expensive if every task requires a car, helper, or appointment battle",
      "Social plans must include low-energy participation, not just active recreation",
    ],
    fieldChecks: [
      "Map the minimum viable week: food, medication, appointments, movement, rest, social contact, and admin",
      "Check heat, smoke, stairs, sidewalks, seating, bathrooms, delivery reliability, and appointment lead times",
      "Define what changes if symptoms flare for two weeks",
    ],
    goodDaySignals: [
      "Short errand loops",
      "Quiet recurring social contact",
      "A home that preserves energy instead of spending it",
    ],
  },
];

export const defenses: Defense[] = [
  {
    id: "irreversible-decision-premortem",
    title: "Irreversible Decision Pre-Mortem",
    category: "planning",
    trigger: "Before quitting work, selling a home, moving states, choosing an expat base, or entering a high-fee community.",
    practicalMoves: [
      "Write the decision as a one-page memo with assumptions, unknowns, and a dated review point",
      "Name the three ways the plan could fail: money, healthcare, loneliness, family conflict, housing, or mobility",
      "Create a reversible trial where possible: rented month, seasonal stay, sabbatical, or staged downsize",
    ],
    specialistBoundary:
      "Use credentialed tax, legal, insurance, medical, and financial professionals for regulated decisions.",
  },
  {
    id: "advisor-conflict-check",
    title: "Advisor Conflict Check",
    category: "scam",
    trigger: "When someone recommends an annuity, investment product, placement, community, insurance plan, or paid referral.",
    practicalMoves: [
      "Ask how the person is paid and whether any referral, commission, or placement fee exists",
      "Request the recommendation, alternatives, fees, surrender terms, and risks in writing",
      "Get a second opinion from a conflict-clean professional before moving money or signing a contract",
    ],
    specialistBoundary:
      "This is a trust and incentive screen, not investment advice or a product recommendation.",
  },
  {
    id: "healthcare-radius-check",
    title: "Healthcare Radius Check",
    category: "risk",
    trigger: "Before choosing a place because it is warm, cheap, beautiful, or socially exciting.",
    practicalMoves: [
      "Map primary care, urgent care, hospital, pharmacy, key specialists, dental, vision, physical therapy, and home health",
      "Check whether access works with the person's actual insurance, language needs, transport, and appointment urgency",
      "Ask what happens after a hospitalization or if one spouse declines faster than the other",
    ],
    specialistBoundary:
      "Use clinicians, insurers, SHIP counselors, brokers, or local care managers for plan-specific guidance.",
  },
  {
    id: "housing-governance-review",
    title: "Housing Governance Review",
    category: "risk",
    trigger: "Before buying into an HOA, condo, active-adult community, mobile-home park, or managed building.",
    practicalMoves: [
      "Read rules, reserves, recent assessments, meeting minutes, pet limits, rental limits, and maintenance obligations",
      "Talk to residents about conflict, responsiveness, noise, culture, and hidden costs",
      "Stress-test monthly life with HOA increases, insurance jumps, repairs, transport, and helper costs",
    ],
    specialistBoundary:
      "Use real estate, legal, insurance, and inspection professionals for transaction-specific review.",
  },
  {
    id: "family-boundary-map",
    title: "Family Boundary Map",
    category: "planning",
    trigger: "When retirement depends on adult children, siblings, a spouse, grandchildren, or a parent in crisis.",
    practicalMoves: [
      "Write down who is responsible for money, rides, medical calls, emergencies, visits, paperwork, and housing backup",
      "Separate nice-to-have family time from care obligations",
      "Create a backup plan that works when the most helpful family member is unavailable",
    ],
    specialistBoundary:
      "Use elder law, mediation, care management, or counseling when conflict, capacity, or legal authority is unclear.",
  },
  {
    id: "digital-financial-hygiene",
    title: "Digital And Financial Hygiene",
    category: "scam",
    trigger: "When a retiree is isolated, recently widowed, moving money, changing advisors, or getting urgent messages.",
    practicalMoves: [
      "Set up password manager, account inventory, trusted contacts, credit freezes where appropriate, and transaction alerts",
      "Create a pause rule for wire transfers, gift cards, crypto, romance requests, emergency calls, and unsolicited tech support",
      "Designate one trusted person for second-look review without handing over unlimited control",
    ],
    specialistBoundary:
      "Use banks, attorneys, fraud hotlines, and law enforcement channels for suspected active exploitation.",
  },
];

export const resourceCategories: ResourceCategory[] = [
  {
    id: "official-baseline",
    title: "Official Baseline Sources",
    useFor: "Rules, eligibility, public benefits, Medicare basics, Social Security basics, visas, and local government services.",
    exampleSources: ["Medicare.gov", "Social Security Administration", "State SHIP programs", "local Area Agencies on Aging", "embassy and immigration sites"],
    questionsToAsk: [
      "Is this source official or just a lead-generation page?",
      "What date, jurisdiction, and eligibility rule does it apply to?",
      "Which specialist should verify the case-specific answer?",
    ],
  },
  {
    id: "listing-inventories",
    title: "Community And Place Inventories",
    useFor: "Seeing what the market makes easy to compare: price, HOA, amenities, location, and lifestyle promise.",
    exampleSources: ["Explore55Plus", "Where55", "local MLS pages", "community websites", "senior apartment directories"],
    questionsToAsk: [
      "What does the listing foreground and what does it hide?",
      "Are resident complaints, healthcare access, transport, and HOA governance visible?",
      "Is this an active-adult product, a care product, or a normal housing product with retirement marketing?",
    ],
  },
  {
    id: "lived-experience-forums",
    title: "Lived Experience Forums",
    useFor: "Pain discovery, regret patterns, practical questions, and real vocabulary from retirees and adult children.",
    exampleSources: ["r/retirement", "r/ExpatFIRE", "r/personalfinance", "r/SameGrassButGreener", "Bogleheads-style discussion forums"],
    questionsToAsk: [
      "Is this a common pattern or one person's unusual case?",
      "What tradeoff keeps repeating across different communities?",
      "What would a sober pre-mortem have caught earlier?",
    ],
  },
  {
    id: "local-operating-system",
    title: "Local Operating-System Sources",
    useFor: "Daily life proof: transport, hospitals, libraries, classes, volunteering, parks, religious groups, senior centers, and home help.",
    exampleSources: ["hospital system directories", "library calendars", "parks and recreation departments", "university extension catalogs", "volunteer match pages", "care manager directories"],
    questionsToAsk: [
      "Can an ordinary week happen with low friction?",
      "What recurring roles exist besides consumer leisure?",
      "What support appears when driving, energy, or spouse status changes?",
    ],
  },
  {
    id: "risk-and-cost-context",
    title: "Risk And Cost Context",
    useFor: "Insurance, climate exposure, housing cost volatility, transportation dependence, and healthcare access constraints.",
    exampleSources: ["county hazard maps", "insurance department pages", "property tax offices", "hospital quality pages", "transit agencies", "local news archives"],
    questionsToAsk: [
      "What cost or climate variable could change the plan fastest?",
      "Which risks are tolerable for a vacation but corrosive for daily life?",
      "What is the backup if the first plan stops working?",
    ],
  },
];

export const bucketListCategories: BucketListCategory[] = [
  {
    id: "learning",
    title: "Learning",
    principle: "Use learning as a recurring identity, not a one-time class purchase.",
    ideas: ["Audit a university course", "Join a language table", "Build a reading seminar with three friends", "Take a practical repair, cooking, or art class"],
    lowFrictionVersion: "One recurring library, online, or community-center class with a named person who expects you back.",
  },
  {
    id: "travel",
    title: "Travel",
    principle: "Design travel around pace, recovery, healthcare access, and repeatable bases.",
    ideas: ["One-month slow stay", "seasonal train trip", "family ancestry route", "two-city healthcare-and-walkability test", "grandchild trip with backup rest days"],
    lowFrictionVersion: "A four-night trip with one anchor activity per day and a medical, transport, and rest plan.",
  },
  {
    id: "service",
    title: "Service",
    principle: "Contribution replaces the mattering that work used to provide.",
    ideas: ["Mentor students", "volunteer at a food pantry", "join a civic board", "help at a hospital desk", "teach a skill at a senior center"],
    lowFrictionVersion: "A two-hour monthly role with a coordinator, clear task, and no open-ended obligation.",
  },
  {
    id: "seasonal-rituals",
    title: "Seasonal Rituals",
    principle: "Retirement needs a calendar architecture after work stops supplying quarters, deadlines, and holidays.",
    ideas: ["Winter walking group", "spring garden weekend", "summer family week", "fall learning sprint", "annual friends reunion"],
    lowFrictionVersion: "One named ritual per season that happens even if energy or money is lower than expected.",
  },
  {
    id: "physical-milestones",
    title: "Physical Milestones",
    principle: "Physical goals should protect future autonomy, not perform youth.",
    ideas: ["Walk a local 5K", "learn balance training", "complete a gentle trail series", "swim twice weekly for a season", "train for a stairs-free travel day"],
    lowFrictionVersion: "A weekly mobility appointment with a route, class, or friend that makes showing up easier.",
  },
  {
    id: "family-friends",
    title: "Family And Friends",
    principle: "Proximity only matters when it becomes ordinary contact.",
    ideas: ["Monthly cousin dinner", "grandchild project day", "old-friend city swap", "story-recording weekend", "shared cooking rotation"],
    lowFrictionVersion: "A standing meal or call that does not require event planning every time.",
  },
  {
    id: "creative-work",
    title: "Creative Work",
    principle: "Make something with a container, audience, and finish line.",
    ideas: ["Photo essay of a neighborhood", "family recipe book", "local history interviews", "small ceramics series", "retirement field-note blog"],
    lowFrictionVersion: "A 12-week project with one deliverable and one person who will see it.",
  },
  {
    id: "low-energy-options",
    title: "Low-Energy Options",
    principle: "A humane bucket list includes days when ambition is low and the body gets a vote.",
    ideas: ["Accessible garden loop", "matinee subscription", "chair yoga", "phone-based friendship practice", "birdwatching bench route", "audio course"],
    lowFrictionVersion: "A menu of three repeatable activities that work on a bad-energy day.",
  },
];

export const fieldNotes: FieldNote[] = [
  {
    id: "where-to-retire-is-not-ranking",
    title: "Where To Retire Is Not A Ranking Problem",
    category: "Place Fit",
    summary:
      "The right place depends on the failure mode that would break the plan first: healthcare, loneliness, mobility, money, climate, family, or identity.",
    body: [
      "Most retirement-location content behaves as if people need a ranked list. Warmest. Cheapest. Best taxes. Most charming downtown. That is useful for browsing and dangerous for commitment.",
      "A retirement place is an operating system. It decides how hard it is to get groceries, see a specialist, make friends, keep moving, recover from a health event, host family, and feel useful on a Tuesday.",
      "Next Chapter starts with failure-mode matching. If healthcare is the gate, hospital and network access outrank scenery. If loneliness is the gate, recurring social infrastructure outranks square footage. If family is the gate, expectations need to be written before the moving truck arrives.",
    ],
  },
  {
    id: "active-adult-brochure-logic",
    title: "The 55+ Brochure Solves One Real Problem And Hides Five Others",
    category: "55+ Communities",
    summary:
      "Active-adult communities package recreation, peers, and social calendars, but the due diligence has to include governance, healthcare, culture fit, and decline optionality.",
    body: [
      "The active-adult listing world is good at making retirement comparable. Price, HOA, golf, pickleball, clubhouse, trails, clubs, gates, pools, and town squares turn anxiety into rows.",
      "That comparison layer captures a real need. Work used to provide repetition, weak ties, and a shared calendar. A good community can replace part of that social machine.",
      "The missing questions are less photogenic: What do residents complain about? How healthy are the HOA reserves? What happens when one spouse stops driving? Are specialists nearby? Is the culture a fit for a solo ager, introvert, renter, or politically mismatched newcomer?",
    ],
  },
  {
    id: "family-proximity-memo",
    title: "Moving Near Family Needs A Memo, Not Just A Feeling",
    category: "Family",
    summary:
      "Family proximity can be powerful, but vague expectations create resentment, dependency, and adult-child overload.",
    body: [
      "Moving near adult children is often framed as obvious: more time with grandchildren, better emergency backup, less distance in a crisis. Sometimes that is exactly right.",
      "The risk is that nobody defines the job. Is the adult child expected to handle rides, medical calls, technology, holidays, money emergencies, home repairs, or future care decisions? Is grandparenting a joy, a role, or a work schedule?",
      "A Next Chapter family memo names the assumptions before the move. It separates ordinary closeness from care obligations and asks whether the location still works if family is unavailable for six weeks.",
    ],
  },
  {
    id: "retire-abroad-backup-plan",
    title: "Retire Abroad Only Counts If The Backup Plan Is Real",
    category: "Retire Abroad",
    summary:
      "Cost and lifestyle comparisons are incomplete without visa, healthcare, language, banking, and return-plan diligence.",
    body: [
      "Retiring abroad can be a serious answer, not just an escape fantasy. Walkability, affordable help, warmer weather, and lively expat networks can materially improve daily life.",
      "But an overseas retirement is a legal and logistical system. Visa renewal, tax residency, insurance eligibility, banking, language, prescription access, and hospital pathways become part of the household infrastructure.",
      "The sober test is not whether the first month feels good. It is whether the plan survives a hospitalization, spouse death, visa rule change, family emergency back home, or a year when novelty wears off.",
    ],
  },
  {
    id: "slow-life-is-not-failure",
    title: "A Slower Life Can Be A Strategy, Not A Retreat",
    category: "Health-Constrained Living",
    summary:
      "For health-constrained people, the retirement question may be how to reduce daily friction now rather than waiting for a traditional age milestone.",
    body: [
      "Some people need a retiree-friendly environment before they are culturally allowed to call it retirement. Fatigue, mobility limits, heat sensitivity, pain, or medical complexity can make ordinary adulthood too expensive in energy.",
      "The right plan may be less dramatic than a dream relocation: fewer stairs, shorter errand loops, better shade, reliable delivery, closer pharmacies, calmer social commitments, and a home that stops draining the week.",
      "Next Chapter treats energy as a real budget. A good day is not the most ambitious day; it is a repeatable day that leaves enough capacity for health, friendship, and small forms of usefulness.",
    ],
  },
  {
    id: "trust-clean-second-opinion",
    title: "The First Retirement Product Is Trust",
    category: "Advice Trust",
    summary:
      "People do not only fear bad decisions; they fear being sold to while trying to make them.",
    body: [
      "Retirement decisions attract advisors, brokers, agents, placement services, communities, insurers, and helpful relatives. Many are legitimate. Some are conflicted. A few are dangerous.",
      "The practical move is not paranoia. It is incentive clarity. Ask how the person is paid, what alternatives were considered, what fees or commissions exist, and what would make the recommendation wrong.",
      "Next Chapter should be explicit about boundaries: research, comparison, and question formation are valuable, but regulated advice belongs with properly credentialed professionals.",
    ],
  },
];

export const siteContent = {
  positioning,
  navigationSections,
  lifestylePaths,
  defenses,
  resourceCategories,
  bucketListCategories,
  fieldNotes,
};
